import { useTranslation } from "react-i18next";
import { Button, Form, OverlayTrigger, Popover, ProgressBar, Spinner } from "react-bootstrap"
import { useEffect, useState } from "react";
import { MdOutlineInfo, MdCheckCircle, MdCancel, MdOutlineVisibilityOff, MdOutlineVisibility } from "react-icons/md";
import { colors } from "../../../../../theme/theme";

import { generatePassword } from "../../../../services/manager/user.service";
import { FieldValues, UseFormSetValue } from "react-hook-form";

interface passwordValidationProps {
    hasLength: boolean;
    hasNumbers: boolean;
    hasCharacter: boolean;
    hasUpperCharacter: boolean;
    hasSpecialCharacter: boolean;
}

const DEFAULT_VALIDATION = {
    hasLength: false,
    hasNumbers: false,
    hasCharacter: false,
    hasUpperCharacter: false,
    hasSpecialCharacter: false,
}

interface IPasswordGenerator { 
    setValue: UseFormSetValue<FieldValues>;
    setStrengthPassword: (strength: number) => void;
    cleanPassword: boolean;
    generatorButton?: boolean;
    className?: string
    
}
export const PasswordGenerator: React.FC<IPasswordGenerator> = ({ setValue, setStrengthPassword, cleanPassword, generatorButton = true, className = "" }): JSX.Element => {
    const { t: translation } = useTranslation();
 

    const [password, setPassword] = useState<string>("");
    const [variant, setVariant] = useState<"success" | "info" | "warning" | "danger">("success");
    const [passwordStrength, setPasswordStrength] = useState<number>(0);
    const [passwordValidation, setPasswordValidation] = useState<passwordValidationProps>(DEFAULT_VALIDATION);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [loadingGeneratorPassword, setLoadingGeneratorPassword] = useState<boolean>(false);

    useEffect(() =>{
        setValue('password', password);
        handleVerifyPassword();
    }, [password])

    useEffect(() =>{
        setStrengthPassword(passwordStrength)
    }, [passwordStrength])

    useEffect(() =>{
        if (cleanPassword) {
            setPassword("")
        }
    }, [cleanPassword])

    const passwordGenerator = () => {
        setLoadingGeneratorPassword(true)
        generatePassword().then((response) => {
            setPassword(response.password)
            setLoadingGeneratorPassword(false)
        })
    }

    const popover = (
        <Popover id="popover-password-validation">
          <Popover.Body>
            <p>Para tornar sua senha forte inclua:</p>
            
            <span className={passwordValidation.hasLength ? "text-success" : "text-danger"}>
                {passwordValidation.hasLength ? <MdCheckCircle className="text-success mb-1 mr-1"/> : <MdCancel className="text-danger mb-1 mr-1"/>}
                {translation('manager.user.minCharacters')}
            </span><br/>
            <span className={passwordValidation.hasCharacter ? "text-success" : "text-danger"}>
                {passwordValidation.hasCharacter ? <MdCheckCircle className="text-success mb-1 mr-1"/> : <MdCancel className="text-danger mb-1 mr-1"/>}
                {translation('manager.user.smallLetters')}
            </span><br/>
            <span className={passwordValidation.hasUpperCharacter ? "text-success" : "text-danger"}>
                {passwordValidation.hasUpperCharacter ? <MdCheckCircle className="text-success mb-1 mr-1"/> : <MdCancel className="text-danger mb-1 mr-1"/>}
                {translation('manager.user.capitalLetters')}
            </span><br/>
            <span className={passwordValidation.hasNumbers ? "text-success" : "text-danger"}>
                {passwordValidation.hasNumbers ? <MdCheckCircle className="text-success mb-1 mr-1"/> : <MdCancel className="text-danger mb-1 mr-1"/>}
                {translation('manager.user.numbers')}
            </span><br/>
            <span className={passwordValidation.hasSpecialCharacter ? "text-success" : "text-danger"}>
                {passwordValidation.hasSpecialCharacter ? <MdCheckCircle className="text-success mb-1 mr-1"/> : <MdCancel className="text-danger mb-1 mr-1"/>}
                {translation('manager.user.specialCharacters')}
            </span>
          </Popover.Body>
        </Popover>
    );

    const handleVerifyPassword = () => {
        if (password.length === 0) {
            setPasswordStrength(0)
            return
        } else {
            let objValidation = {
                hasLength: password.length >= 8 ? true : false,
                hasNumbers: /\d/.test(password),
                hasCharacter: /[a-z]/.test(password),
                hasUpperCharacter: /[A-Z]/.test(password),
                hasSpecialCharacter: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
            }
    
            if (objValidation.hasLength) {
                if ((objValidation.hasNumbers && !objValidation.hasSpecialCharacter && !objValidation.hasUpperCharacter && !objValidation.hasCharacter) || 
                    (objValidation.hasCharacter && !objValidation.hasSpecialCharacter && !objValidation.hasUpperCharacter && !objValidation.hasNumbers)) { //only numbers or only characters
                    setPasswordStrength(25)
                    setVariant('danger')
                } else if (objValidation.hasCharacter && objValidation.hasNumbers && !objValidation.hasSpecialCharacter && !objValidation.hasUpperCharacter) { //only numbers and characters
                    setPasswordStrength(50)
                    setVariant('warning')
                } else if (objValidation.hasCharacter && objValidation.hasNumbers && objValidation.hasUpperCharacter && !objValidation.hasSpecialCharacter) { //numbers, characters and upperCase characters
                    setPasswordStrength(75)
                    setVariant('info')
                } else if (objValidation.hasCharacter && objValidation.hasNumbers && objValidation.hasUpperCharacter && objValidation.hasSpecialCharacter) { //all items
                    setPasswordStrength(100)
                    setVariant('success')
                }
            } else {
                setPasswordStrength(25)
                setVariant('danger')
            }
    
            setPasswordValidation(objValidation)
        }
        
    }

    return (  
        <Form.Group className={className}>
            <Form.Label className={generatorButton ? "text-darker-grey w-100" : "text-black w-100"}>
                {
                    generatorButton ? 
                        translation('manager.user.password') 
                    : (
                        <b>{translation('manager.user.password')}</b>
                    )
                }
            </Form.Label><br/>
            {
                generatorButton &&
                <>
                    
                    <Button variant="outline-primary" type="button" size="sm" className="btn-rounded mb-2" onClick={() => passwordGenerator()}>
                        {translation('manager.user.generatePassword')}
                    </Button>
                </>
            }
            {
                loadingGeneratorPassword &&
                <Spinner animation="border" variant="primary" style={{ width: '1.2rem', height: '1.2rem', borderWidth: '0.15em', borderRightColor: 'transparent', marginLeft: '5px'}} />
            }
            <Form.Control type={showPassword ? "text" : "password"} value={password} placeholder={translation('manager.user.password')} onChange={(e: any) =>setPassword(e.target.value)}/>
            <span className={generatorButton ? "eye-password-manger-user" : "eye-password-icon-login" }>
                {
                    showPassword ? (
                        <MdOutlineVisibility style={{ width: "22px", height: "22px", color: colors.greyscale.black }} onClick={() => setShowPassword(false)} />
                    ) : (
                        <MdOutlineVisibilityOff style={{ width: "22px", height: "22px", color: colors.greyscale.black }} onClick={() => setShowPassword(true)} />
                    )
                }
            </span>
            {
                password && (
                    <>
                        <ProgressBar className="mt-2 mb-2" variant={variant} now={passwordStrength} label="" visuallyHidden style={{ height: '7px' }}/>
                        
                        <div className="text-end">
                            <OverlayTrigger placement="bottom" overlay={popover}>
                                <span className={
                                    passwordStrength <= 25 ? "text-danger" : 
                                        passwordStrength > 25 && passwordStrength <= 50 ? "text-warning" : 
                                            passwordStrength > 50 && passwordStrength <= 75 ? "text-info" : "text-success" 
                                }>
                                    <MdOutlineInfo className="mb-1 mr-1"/> 
                                    {
                                        passwordStrength <= 25 ? "Fraca" : 
                                            (passwordStrength > 25 && passwordStrength <= 50 ? "Média" : 
                                                (passwordStrength > 50 && passwordStrength <= 75 ? "Boa" : "Forte")
                                            )                                           
                                    }
                                </span>
                            </OverlayTrigger>
                        </div>
                    </>
                )
            }
        </Form.Group>
    )
}